<script setup lang="ts">
  // Vue core imports
  import { computed, onMounted, ref, watch } from 'vue'

  // Internal imports
  import CPButton from '@ui/components/CPButton.vue'

  import CPForm from '@ui/components/CPForm.vue'

  import CPFormItem from '@ui/components/CPFormItem.vue'

  import CPInput from '@ui/components/CPInput.vue'

  import CPText from '@ui/components/CPText.vue'

  import { useI18n } from 'vue-i18n'
  import { createElementId } from '../../../core-layer/utils'

  import { useAuth } from '../../composables/useAuth'

  import { Screens } from '../../types/auth'

  import { getErrorMessage } from '../../utils'

  import Recaptcha from '../captcha/ReCaptcha.vue'

  // Props definitions
  const props = defineProps({
    email: {
      type: String,
      default: '',
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    (e: 'changeScreen', value: any): void
  }>()

  const { t } = useI18n()

  // Refs and reactive variables
  const errorMessage = ref()

  const loading = ref(false)

  const showRecaptcha = ref(false)

  const reCaptchaToken = ref(undefined)

  const loginDisabled = ref(false)

  const emailError = ref('')

  const rules = {
    email: [
      {
        required: true,
        message: 'Email is required',
        trigger: ['input', 'blur'],
      },
      {
        type: 'email',
        message: 'Please enter a valid email',
        trigger: ['input', 'blur'],
      },
    ],
  }

  const formValue = ref({
    email: props.email || '',
  })

  // Computed properties
  const isFormValid = computed(() => {
    return formValue.value.email && !emailError.value
  })

  // Refs
  const formRef = ref()

  const handleValidateClick = async () => {
    loading.value = true
    const result = await validateForm()
    if (!result) {
      loading.value = false
      return
    }

    try {
      const auth = useAuth()
      await auth.migrateEmail(formValue.value.email.trim())

      const response = await auth.generateOTPForLogin(formValue.value.email.trim(), 'login', reCaptchaToken.value)

      const otpToken = response?.data?.otpToken
      if (otpToken) {
        emit('changeScreen', {
          email: formValue.value.email,
          otpToken,
          page: Screens.VERIFY_SECURE_CODE,
          action: 'login',
        })
      } else {
        errorMessage.value = t('auth.messages.failedToGenerateSecureCode')
      }
      showRecaptcha.value = false
    } catch (error: any) {
      if (error?.response?.status === 429) {
        loginDisabled.value = true
        showRecaptcha.value = true
      } else {
        showRecaptcha.value = false
        errorMessage.value = getErrorMessage(error)
      }
    } finally {
      loading.value = false
    }
  }

  const onFormValueChange = () => {
    errorMessage.value = null
  }

  const onVerify = (response: any) => {
    reCaptchaToken.value = response
    loginDisabled.value = false
  }

  const onError = (response: any) => {
    loginDisabled.value = true
  }

  const onExpired = (response: any) => {
    reCaptchaToken.value = undefined
    loginDisabled.value = true
  }

  // Lifecycle hooks
  onMounted(() => {
    if (props.email) {
      formValue.value.email = props.email
    }
  })

  // Watch effects
  watch(
    () => props.email,
    (newEmail: any) => {
      if (newEmail) {
        formValue.value.email = newEmail
      }
    }
  )

  watch(
    () => formValue.value.email,
    () => {
      emailError.value = ''
      errorMessage.value = null
    }
  )

  // User-defined functions
  async function validateForm() {
    try {
      await formRef.value.getForm().validate()
      return true
    } catch (e) {
      console.error('error', e)
      return false
    }
  }
</script>

<template>
  <CPForm
    :id="createElementId('login-secure', 'modal', 'login-secure-form')"
    ref="formRef"
    class="flex flex-col items-center"
    :model="formValue"
    :rules="rules"
    @keyup.enter="handleValidateClick"
  >
    <CPFormItem class="w-full" :label="t('auth.email')" required path="email">
      <CPInput
        :id="createElementId('login-secure', 'modal', 'email-input')"
        v-model="formValue.email"
        size="sm"
        :placeholder="t('auth.emailPlaceholder')"
        @input="onFormValueChange()"
      />
      <template #feedback>
        <CPText size="sm" :class="emailError ? 'text-danger' : 'text-clientportal-font-secondary'">
          {{ emailError || t('auth.secureCodeSentToEmail') }}
        </CPText>
      </template>
    </CPFormItem>

    <div v-if="errorMessage" class="text-danger w-full">
      <CPText size="sm">{{ errorMessage }}</CPText>
    </div>

    <Recaptcha
      v-if="showRecaptcha"
      :id="createElementId('login-secure', 'modal', 'recaptcha')"
      class="flex w-full justify-center"
      @expired="onExpired"
      @verify="onVerify"
      @error="onError"
    />

    <CPButton
      :id="createElementId('login-secure', 'modal', 'generate-code-button')"
      class="w-full rounded-lg"
      type="primary"
      size="md"
      :loading="loading"
      :disabled="loading || loginDisabled || !isFormValid"
      @click="handleValidateClick"
    >
      {{ t('auth.generateSecureCode') }}
    </CPButton>

    <CPButton
      :id="createElementId('login-secure', 'modal', 'login-with-password-button')"
      class="w-full rounded-lg"
      type="secondary"
      size="md"
      @click="emit('changeScreen', { page: Screens.LOGIN })"
    >
      {{ t('auth.loginWithPassword') }}
    </CPButton>

    <div class="text-clientportal-primary flex w-full justify-center">
      <CPText size="md" weight="semibold">
        {{ t('auth.newUser') }}
        <span
          :id="createElementId('login-secure', 'modal', 'open-signup-button')"
          class="cursor-pointer hover:underline"
          @click="emit('changeScreen', { page: Screens.SIGN_UP })"
        >
          {{ t('auth.signUp') }}
        </span>
      </CPText>
    </div>
  </CPForm>
</template>

<style scoped>
  .hr-divider:not(.hr-divider--vertical) {
    margin: 0px;
  }
  .hr-form {
    gap: 12px;
  }
</style>
